import { useEffect, useRef, useMemo } from 'react';
import { highlightBracketedText, highlightUnderscoreText } from 'src/utils/utils';
import { ImageAndTextFieldsFragment } from 'src/generated/graphql';
import { useLocation } from 'react-router-dom';

type AutomaticContentProps = {
  content: ImageAndTextFieldsFragment;
  className?: string;
};

// Helper function to extract bullet point data with hyperlink info
const extractBulletPointData = (content: any) => {
  const bulletPoints = content?.json?.content
    ?.filter((val: any) => val.nodeType === 'unordered-list')[0]
    ?.content?.map((item: any) => {
      const paragraph = item?.content?.[0];
      if (!paragraph || paragraph.nodeType !== 'paragraph') return null;
      
      // Check if there's a hyperlink in the paragraph content
      const hyperlinkItem = paragraph.content?.find((item: any) => item.nodeType === 'hyperlink');
      
      if (hyperlinkItem) {
        // Extract text and URL from hyperlink
        const text = hyperlinkItem.content?.[0]?.value || '';
        const url = hyperlinkItem.data?.uri || '';
        return { text, url, hasLink: true };
      } else {
        // Regular text without hyperlink
        const text = paragraph.content?.[0]?.value || '';
        return { text, url: null, hasLink: false };
      }
    })
    ?.filter(Boolean) || [];
    
  return bulletPoints;
};

const ProductContent = ({ content, className = '' }: AutomaticContentProps) => {
  const { eyebrowText, title, imageDescriptionJson } = content;
  console.log("content", content);
  
  const bulletPointsData = extractBulletPointData(imageDescriptionJson);
  
  const midIndex = Math.ceil(bulletPointsData.length / 2);
  const firstColumn = bulletPointsData.slice(0, midIndex);
  const secondColumn = bulletPointsData.slice(midIndex);
  
  const location = useLocation();
  const pathParts = location.pathname.toLowerCase().split('/').filter(Boolean);
  const isRouteIndustry = pathParts.length === 1 && pathParts[0] === 'industries';
  
  // Use refs to track if forms have been created to prevent duplicates
  const formsCreatedRef = useRef(false);
  const scriptLoadedRef = useRef(false);
  
  // Create unique IDs for this component instance
  const uniqueId = useMemo(() => Math.random().toString(36).substr(2, 9), []);
  const eventFormId = `eventForm-${uniqueId}`;
  const hubspotFormId = `hubspotForm-${uniqueId}`;

  useEffect(() => {
    // Prevent multiple executions
    if (formsCreatedRef.current) return;

    const loadHubSpotAndCreateForms = () => {
      // Check if HubSpot script is already loaded
      const existingScript = document.querySelector('script[src*="js.hsforms.net/forms"]');
      
      if (!existingScript && !scriptLoadedRef.current) {
        scriptLoadedRef.current = true;
        const script = document.createElement('script');
        script.src = 'https://js.hsforms.net/forms/embed/45649448.js';
        script.id = 'hubspot-forms-script';
        
        script.onload = () => {
          createHubSpotForms();
        };
        
        script.onerror = () => {
          console.error('Failed to load HubSpot script');
          scriptLoadedRef.current = false;
        };
        
        document.head.appendChild(script);
      } else if ((window as any).hbspt) {
        // Script already exists and HubSpot is available
        createHubSpotForms();
      } else if (existingScript) {
        // Script exists but HubSpot might not be ready yet
        const checkHubSpot = setInterval(() => {
          if ((window as any).hbspt) {
            clearInterval(checkHubSpot);
            createHubSpotForms();
          }
        }, 100);
        
        // Clear interval after 10 seconds to prevent infinite checking
        setTimeout(() => clearInterval(checkHubSpot), 10000);
      }
    };

    const createHubSpotForms = () => {
      // Prevent multiple form creation
      if (formsCreatedRef.current) return;
      
      try {
        // Only create the specific form based on className
        if (className === 'promise-event-form') {
          const eventFormContainer = document.getElementById(eventFormId);
          
          // Clear existing form first
          if (eventFormContainer) {
            eventFormContainer.innerHTML = '';
            
            // Create the event form only
            if ((window as any).hbspt) {
              (window as any).hbspt.forms.create({
                className: 'hs-form-frame',
                region: 'na1',
                portalId: '45649448',
                formId: '6812734a-d5b9-4847-a760-4d77732c462e',
                target: `#${eventFormId}`,
              });
            }
          }
        } else {
          const hubspotFormContainer = document.getElementById(hubspotFormId);
          
          // Clear existing form first
          if (hubspotFormContainer) {
            hubspotFormContainer.innerHTML = '';
            
            // Create the main form only
            if ((window as any).hbspt) {
              (window as any).hbspt.forms.create({
                portalId: '45649448',
                formId: 'c1414505-f881-4b8e-9a43-6c4db2e34704',
                target: `#${hubspotFormId}`,
              });
            }
          }
        }
        
        formsCreatedRef.current = true;
      } catch (error) {
        console.error('Error creating HubSpot forms:', error);
      }
    };

    loadHubSpotAndCreateForms();

    // Cleanup function
    return () => {
      // Clear forms when component unmounts
      const hubspotFormContainer = document.getElementById('hubspotForm');
      const eventFormContainer = document.getElementById('eventForm');
      
      if (hubspotFormContainer) {
        hubspotFormContainer.innerHTML = '';
      }
      if (eventFormContainer) {
        eventFormContainer.innerHTML = '';
      }
      
      // Reset refs for potential remount
      formsCreatedRef.current = false;
    };
  }, []); // Empty dependency array - only run once

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js.hsforms.net/forms/embed/45649448.js';
    document.body.appendChild(script);

    script.addEventListener('load', () => {
      if ((window as any).hbspt) {
        (window as any).hbspt.forms.create({
          className: 'hs-form-frame',
          region: 'na1',
          portalId: '45649448',
          formId: '6812734a-d5b9-4847-a760-4d77732c462e',
          target: '#eventForm',
        });
      }
    });
  }, []);

  // Component to render bullet point item with optional link
  const BulletPointItem = ({ item, index, columnPrefix }: { item: any, index: number, columnPrefix: string }) => {
    const bulletIcon = (
      <svg
        width="24"
        height="29"
        viewBox="0 0 24 29"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M22.91 14.4091C22.91 20.434 18.0258 25.3182 12.0009 25.3182C5.97596 25.3182 1.0918 20.434 1.0918 14.4091C1.0918 8.38417 5.97596 3.5 12.0009 3.5C18.0258 3.5 22.91 8.38417 22.91 14.4091Z"
          fill="#CDCDF3"
          fillOpacity="0.25"
        />
        <path
          d="M22.91 14.4091C22.91 20.434 18.0258 25.3182 12.0009 25.3182C5.97596 25.3182 1.0918 20.434 1.0918 14.4091C1.0918 8.38417 5.97596 3.5 12.0009 3.5C18.0258 3.5 22.91 8.38417 22.91 14.4091Z"
          fill="#CDCDF3"
          fillOpacity="0.25"
        />
        <path
          d="M16.91 11.1364L10.3645 17.6818L7.63725 14.9545M16.91 11.1364L10.3645 17.6818M22.91 14.4091C22.91 20.434 18.0258 25.3182 12.0009 25.3182C5.97596 25.3182 1.0918 20.434 1.0918 14.4091C1.0918 8.38417 5.97596 3.5 12.0009 3.5C18.0258 3.5 22.91 8.38417 22.91 14.4091ZM22.91 14.4091C22.91 20.434 18.0258 25.3182 12.0009 25.3182C5.97596 25.3182 1.0918 20.434 1.0918 14.4091C1.0918 8.38417 5.97596 3.5 12.0009 3.5C18.0258 3.5 22.91 8.38417 22.91 14.4091Z"
          stroke="#CDCDF3"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16.9094 11.1348L10.364 17.6802L7.63672 14.9529"
          stroke="#8773DA"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );

    if (item.hasLink && item.url) {
      return (
        <li key={`${columnPrefix}-${index}`}>
          {bulletIcon}
          <a 
            href={item.url} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              textDecoration: 'none', 
              color: 'inherit',
              cursor: 'pointer'
            }}
          >
            <span>{item.text}</span>
          </a>
        </li>
      );
    } else {
      return (
        <li key={`${columnPrefix}-${index}`}>
          {bulletIcon}
          <span>{item.text}</span>
        </li>
      );
    }
  };

  // Component for regular bullet points (non-event form)
  const RegularBulletPointItem = ({ item, index, columnPrefix }: { item: any, index: number, columnPrefix: string }) => {
    const bulletIcon = (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M16.909 8.72725L10.3635 15.2727L7.63628 12.5454M16.909 8.72725L10.3635 15.2727M22.909 12C22.909 18.0249 18.0248 22.9091 11.9999 22.9091C5.97499 22.9091 1.09082 18.0249 1.09082 12C1.09082 5.97505 5.97499 1.09088 11.9999 1.09088C18.0248 1.09088 22.909 5.97505 22.909 12Z"
          stroke="#8773DA"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="#CDCDF3"
        />
        <path
          d="M16.909 8.72729L10.3635 15.2727L7.63623 12.5455"
          stroke="#8773DA"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );

    if (item.hasLink && item.url) {
      return (
        <li key={`${columnPrefix}-${index}`}>
          {bulletIcon}
          <a 
            href={item.url} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              textDecoration: 'none', 
              color: 'inherit',
              cursor: 'pointer'
            }}
          >
            <span>{item.text}</span>
          </a>
        </li>
      );
    } else {
      return (
        <li key={`${columnPrefix}-${index}`}>
          {bulletIcon}
          <span>{item.text}</span>
        </li>
      );
    }
  };

  return (
    <>
      <div className="promise-section">
        <div className="container-fluid custom-spacer-col">
          {className === 'promise-event-form' ? (
            <div
              className={`promise-inner-section ${isRouteIndustry && 'promise-inner-section-industry'}`}
            >
              <div className="promise-form-left">
                <div className="promise-left promise-industry">
                  <h2
                    className="highlight-bracket-text"
                    dangerouslySetInnerHTML={{
                      __html: highlightBracketedText(eyebrowText ?? '', 'bold'),
                    }}
                    style={{ textAlign: 'start' }}
                  />

                  <div className="promise-description">
                    <p
                      dangerouslySetInnerHTML={{
                        __html: highlightUnderscoreText(title ?? ''),
                      }}
                    />
                  </div>
                </div>
                <div className="promise-right">
                  <ul className="promise-list">
                    {firstColumn.map((item, index) => (
                      <BulletPointItem 
                        key={`col1-${index}`}
                        item={item} 
                        index={index} 
                        columnPrefix="col1" 
                      />
                    ))}
                    {secondColumn.map((item, index) => (
                      <BulletPointItem 
                        key={`col2-${index}`}
                        item={item} 
                        index={index} 
                        columnPrefix="col2" 
                      />
                    ))}
                  </ul>
                </div>
              </div>
              <div className="promise-form">
                <div 
                  className="hs-form-frame" 
                  data-region="na1" 
                  data-form-id="6812734a-d5b9-4847-a760-4d77732c462e" 
                  data-portal-id="45649448"
                ></div>
              </div>
            </div>
          ) : (
            <div
              className={`promise-inner-section ${isRouteIndustry && 'promise-inner-section-industry'}`}
            >
              <div className="promise-left promise-industry">
                <h2
                  className="highlight-bracket-text"
                  dangerouslySetInnerHTML={{
                    __html: highlightBracketedText(eyebrowText ?? '', 'bold'),
                  }}
                  style={{ textAlign: 'start' }}
                />

                <div className="promise-description">
                  <p
                    dangerouslySetInnerHTML={{
                      __html: highlightUnderscoreText(title ?? ''),
                    }}
                  />
                </div>
              </div>
              <div className="promise-right">
                {/* First Column */}
                <ul className="promise-list">
                  {firstColumn.map((item, index) => (
                    <RegularBulletPointItem 
                      key={`col1-${index}`}
                      item={item} 
                      index={index} 
                      columnPrefix="col1" 
                    />
                  ))}
                </ul>

                {/* Second Column */}
                <ul className="promise-list">
                  {secondColumn.map((item, index) => (
                    <RegularBulletPointItem 
                      key={`col2-${index}`}
                      item={item} 
                      index={index} 
                      columnPrefix="col2" 
                    />
                  ))}
                </ul>
                
                {/* HubSpot Form Container for regular form */}
                {/* <div id={hubspotFormId}></div> */}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProductContent;